import useSWR, { mutate as globalSWRMutate } from 'swr';
import { fetcherWithMethod } from 'utils/axios';
import { handleApiError } from 'utils/errorHandler';
import { AxiosRequestConfig } from 'axios';

/**
 * Hook to fetch files list by project ID / reference ID.
 * Endpoint: GET {baseURL}/{moduleKey}/GetList/{referenceID}
 */
export const useGetDirectoryAndFiles = (moduleKey: string) => {
  if (!moduleKey) return { getDirectoryAndFiles: () => Promise.reject(new Error('Module key is required')) };
  const { mutate } = useSWR(moduleKey);
  const getDirectoryAndFiles = async (referenceID: any) => {
    try {
      const response = await fetcherWithMethod('GET', [`${moduleKey}/GetList/${referenceID}`, { method: 'GET' }]);
      mutate();
      return response;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };
  return { getDirectoryAndFiles };
};

/**
 * Hook to create a new directory.
 * Endpoint: POST {baseURL}/{moduleKey}/CreateDirectory
 */
export const useCreateDirectory = (moduleKey: string) => {
  if (!moduleKey) return { createDirectory: () => Promise.reject(new Error('Module key is required')) };
  const createDirectory = async (data: any) => {
    try {
      const response = await fetcherWithMethod('POST', [
        `${moduleKey}/CreateDirectory`,
        { data } as AxiosRequestConfig
      ]);
      return response;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };
  return { createDirectory };
};

/**
 * Hook to rename an existing directory.
 * Endpoint: PUT {baseURL}/{moduleKey}/RenameDirectory
 */
export const useRenameDirectory = (moduleKey: string) => {
  if (!moduleKey) return { renameDirectory: () => Promise.reject(new Error('Module key is required')) };
  const renameDirectory = async (data: any) => {
    try {
      const response = await fetcherWithMethod('PUT', [
        `${moduleKey}/RenameDirectory`,
        { data } as AxiosRequestConfig
      ]);
      return response;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };
  return { renameDirectory };
};

/**
 * Hook to delete directories and files.
 * Endpoint: DELETE {baseURL}/{moduleKey}/DeleteDirectoryAndFiles
 */
export const useDeleteDirectoryAndFiles = (moduleKey: string) => {
  if (!moduleKey) return { deleteDirectoryAndFiles: () => Promise.reject(new Error('Module key is required')) };
  const { mutate } = useSWR(moduleKey);
  const deleteDirectoryAndFiles = async (data: {
    referenceID: number | string;
    directories: string[];
    files: string[];
  }) => {
    try {
      const response = await fetcherWithMethod('DELETE', [
        `${moduleKey}/DeleteDirectoryAndFiles`,
        { data, timeout: 0 } as AxiosRequestConfig
      ]);
      mutate();

      globalSWRMutate((key: any) => {
        const isProjectRelated = moduleKey.startsWith('Project/');
        const matches = (keyStr: string) => {
          if (keyStr.startsWith(moduleKey)) return true;
          if (isProjectRelated && keyStr.startsWith('Project/') && keyStr.includes('FileManager')) {
            return true;
          }
          return false;
        };

        if (typeof key === 'string') {
          return matches(key);
        }
        if (Array.isArray(key) && typeof key[0] === 'string') {
          return matches(key[0]);
        }
        return false;
      });

      return response;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };
  return { deleteDirectoryAndFiles };
};

/**
 * Hook to upload files.
 * Endpoint: POST {baseURL}/{moduleKey}/UploadFiles
 */
export const useUploadFiles = (moduleKey: string) => {
  if (!moduleKey) return { uploadFiles: () => Promise.reject(new Error('Module key is required')) };
  const { mutate } = useSWR(moduleKey);
  const uploadFiles = async (data: {
    referenceID: number | string;
    directoryName: string | null;
    files: File[] | FileList | File;
  }) => {
    try {
      const formData = new FormData();
      formData.append('ReferenceID', data.referenceID.toString());
      formData.append('DirectoryName', data.directoryName ?? "");

      if (data.files) {
        if (Array.isArray(data.files) || data.files instanceof FileList) {
          for (let i = 0; i < data.files.length; i++) {
            formData.append('Files', data.files[i]);
          }
        } else {
          formData.append('Files', data.files);
        }
      }

      const response = await fetcherWithMethod('POST', [
        `${moduleKey}/UploadFiles`,
        {
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 0
        } as AxiosRequestConfig
      ]);

      globalSWRMutate((key: any) => {
        const isProjectRelated = moduleKey.startsWith('Project/');
        const matches = (keyStr: string) => {
          if (keyStr.startsWith(moduleKey)) return true;
          if (isProjectRelated && keyStr.startsWith('Project/') && keyStr.includes('FileManager')) {
            return true;
          }
          return false;
        };

        if (typeof key === 'string') {
          return matches(key);
        }
        if (Array.isArray(key) && typeof key[0] === 'string') {
          return matches(key[0]);
        }
        return false;
      });

      return response;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };
  return { uploadFiles };
};

/**
 * Hook to update file notes.
 * Endpoint: PUT {baseURL}/{moduleKey}/UpdateFileNotes
 */
export const useUpdateFileNotes = (moduleKey: string) => {
  if (!moduleKey) return { updateFileNotes: () => Promise.reject(new Error('Module key is required')) };
  const { mutate } = useSWR(moduleKey);
  const updateFileNotes = async (data: {
    referenceID: number | string;
    fileName: string;
    notes: string;
  }) => {
    const { referenceID, fileName, notes } = data;
    try {
      const response = await fetcherWithMethod('PUT', [
        `${moduleKey}/UpdateFileNotes/${referenceID}`,
        { data: { fileName, notes } } as AxiosRequestConfig
      ]);
      mutate();

      globalSWRMutate((key: any) => {
        const isProjectRelated = moduleKey.startsWith('Project/');
        const matches = (keyStr: string) => {
          if (keyStr.startsWith(moduleKey)) return true;
          if (isProjectRelated && keyStr.startsWith('Project/') && keyStr.includes('FileManager')) {
            return true;
          }
          return false;
        };

        if (typeof key === 'string') {
          return matches(key);
        }
        if (Array.isArray(key) && typeof key[0] === 'string') {
          return matches(key[0]);
        }
        return false;
      });

      return response;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };
  return { updateFileNotes };
};

/**
 * Hook to get the URL for a file.
 * Endpoint: GET {baseURL}/{moduleKey}/GetFileUrl/{referenceID}?fileName={fileName}
 */
export const useGetFileUrl = (moduleKey: string) => {
  if (!moduleKey) return { getFileUrl: () => Promise.reject(new Error('Module key is required')) };
  const getFileUrl = async (referenceID: number | string, fileName: string) => {
    try {
      const queryParams = new URLSearchParams({ fileName }).toString();
      const response = await fetcherWithMethod('GET', `${moduleKey}/GetFileUrl/${referenceID}?${queryParams}`);
      return response;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };
  return { getFileUrl };
};

/**
 * Hook to get URLs for multiple files.
 * Endpoint: POST {baseURL}/{moduleKey}/GetFileUrls/{referenceID}
 */
export const useGetFileUrls = (moduleKey: string) => {
  if (!moduleKey) return { getFileUrls: () => Promise.reject(new Error('Module key is required')) };
  const getFileUrls = async (referenceID: number | string, data: { files: string[] }) => {
    try {
      const response = await fetcherWithMethod('POST', [
        `${moduleKey}/GetFileUrls/${referenceID}`,
        { data } as AxiosRequestConfig
      ]);
      return response;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };
  return { getFileUrls };
};

/**
 * Hook to rename files.
 * Endpoint: PUT {baseURL}/{moduleKey}/RenameFile/{referenceID}
 */
export const useRenameFile = (moduleKey: string) => {
  if (!moduleKey) return { renameFile: () => Promise.reject(new Error('Module key is required')) };
  const renameFile = async (
    referenceID: number | string,
    data: {
      directoryName: string;
      sourceFileName: string[];
      newFileName: string;
    }
  ) => {
    try {
      const response = await fetcherWithMethod('PUT', [
        `${moduleKey}/RenameFile/${referenceID}`,
        { data, timeout: 0 } as AxiosRequestConfig
      ]);
      // Note: mutate/globalSWRMutate intentionally removed.
      // Blob storage update mein time lagta hai, isliye rename ke baad
      // GetList auto-call nahi hoti. CompressedFilemanager optimistic
      // local state update karta hai instead.
      return response;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };
  return { renameFile };
};

/**
 * Hook to move directory and files.
 * Endpoint: PUT {baseURL}/{moduleKey}/Move
 */
export const useMoveDirectoryAndFiles = (moduleKey: string) => {
  if (!moduleKey) return { moveDirectoryAndFiles: () => Promise.reject(new Error('Module key is required')) };
  const { mutate } = useSWR(moduleKey);
  const moveDirectoryAndFiles = async (data: any) => {
    try {
      const response = await fetcherWithMethod('PUT', [
        `${moduleKey}/Move`,
        { data, timeout: 0 } as AxiosRequestConfig
      ]);
      mutate();

      globalSWRMutate((key: any) => {
        const isProjectRelated = moduleKey.startsWith('Project/');
        const matches = (keyStr: string) => {
          if (keyStr.startsWith(moduleKey)) return true;
          if (isProjectRelated && keyStr.startsWith('Project/') && keyStr.includes('FileManager')) {
            return true;
          }
          return false;
        };

        if (typeof key === 'string') {
          return matches(key);
        }
        if (Array.isArray(key) && typeof key[0] === 'string') {
          return matches(key[0]);
        }
        return false;
      });

      return response;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };
  return { moveDirectoryAndFiles };
};

/**
 * SWR Hook to fetch files/directories list reactively.
 * Endpoint: GET {baseURL}/{moduleKey}/GetList/{referenceID}
 */
export const useGetDirectoryAndFilesList = (referenceID: number | undefined, moduleKey: string) => {
  const endpoint = referenceID ? `${moduleKey}/GetList/${referenceID}` : null;
  const { data, error, isLoading, mutate } = useSWR<any[]>(
    endpoint ? [endpoint, { method: 'GET' }] : null,
    (args: [string, any]) => fetcherWithMethod('GET', args),
    { revalidateIfStale: true, revalidateOnFocus: false, revalidateOnReconnect: false, onError: handleApiError }
  );
  return { files: data || [], error, isLoading, mutate };
};

/**
 * Hook to generate upload URLs.
 * Endpoint: POST {baseURL}/{moduleKey}/GenerateUploadUrls
 */
export const useGenerateUploadUrls = (moduleKey: string) => {
  if (!moduleKey) return { generateUploadUrls: () => Promise.reject(new Error('Module key is required')) };
  const generateUploadUrls = async (data: {
    referenceID: number | string;
    directoryName: string | null;
    files: string[];
  }) => {
    try {
      const response = await fetcherWithMethod('POST', [
        `${moduleKey}/GenerateUploadUrls`,
        { data } as AxiosRequestConfig
      ]);
      return response;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };
  return { generateUploadUrls };
};

/**
 * Hook to notify after a successful file upload.
 * Endpoint: POST {baseURL}/{moduleKey}/AfterSuccessfulFileUpload
 */
export const useAfterSuccessfulFileUpload = (moduleKey: string) => {
  if (!moduleKey) return { afterSuccessfulFileUpload: () => Promise.reject(new Error('Module key is required')) };
  const { mutate } = useSWR(moduleKey);
  const afterSuccessfulFileUpload = async (data: {
    referenceID: number | string;
    directoryName: string | null;
    fileName: string;
  }) => {
    try {
      const response = await fetcherWithMethod('POST', [
        `${moduleKey}/AfterSuccessfulFileUpload`,
        { data } as AxiosRequestConfig
      ]);
      mutate();

      globalSWRMutate((key: any) => {
        const isProjectRelated = moduleKey.startsWith('Project/');
        const matches = (keyStr: string) => {
          if (keyStr.startsWith(moduleKey)) return true;
          if (isProjectRelated && keyStr.startsWith('Project/') && keyStr.includes('FileManager')) {
            return true;
          }
          return false;
        };

        if (typeof key === 'string') {
          return matches(key);
        }
        if (Array.isArray(key) && typeof key[0] === 'string') {
          return matches(key[0]);
        }
        return false;
      });

      return response;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };
  return { afterSuccessfulFileUpload };
};




